/*
  Maui X Lolita — Back in Stock drawer logic.

  - One <mxl-bis-drawer> per product page; moved to <body> on connect so both
    the main-form trigger and the sticky-bar trigger open the same instance.
    A duplicate instance (e.g. re-injected by a section re-render) removes
    itself; trigger clicks are DELEGATED at the document level so re-rendered
    buttons keep working.
  - Variant state has one authoritative source, re-resolved at page load,
    on the theme's variantChange event, when the drawer opens, and again
    immediately before submission: the main product form's hidden
    input[name="id"] (kept current for main, sticky, and installment forms by
    VariantSelects.updateVariantInput in global.js), validated against the
    embedded product-variants JSON ([data-all-variants-no-high]).
    NOTE: PUB_SUB_EVENTS is a top-level `const` in constants.js, so it is NOT
    a window property — it must be referenced as a bare global. Checking
    window.PUB_SUB_EVENTS silently disabled the variant sync (the bug that
    froze submissions on the first available variant).
  - Submission is BLOCKED (with a safe message) when the resolved variant is
    missing, unresolvable, or currently purchasable (available variants and
    continue-selling variants are never valid Back in Stock targets).
  - Submits to Klaviyo's PUBLIC client Back in Stock endpoint with the public
    company ID only. The private Klaviyo API key is never used or referenced.
  - Marketing consent is a SEPARATE Klaviyo client subscription call, made
    only when the checkbox is checked, with an explicit custom_source. A Back
    in Stock request alone never creates marketing consent.
*/

if (!customElements.get("mxl-bis-drawer")) {
  customElements.define(
    "mxl-bis-drawer",
    class MxlBisDrawer extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector(".mxl-bis-form");
        this.emailInput = this.querySelector(".mxl-bis-email");
        this.consentInput = this.querySelector(".mxl-bis-consent-checkbox");
        this.submitBtn = this.querySelector(".mxl-bis-submit");
        this.statusEl = this.querySelector(".mxl-bis-status");
        this.optionsEl = this.querySelector(".mxl-bis-options");
        this.dialogEl = this.querySelector('[role="dialog"]');

        this.querySelectorAll(".mxl-bis-close, .mxl-bis-overlay").forEach(
          (el) => el.addEventListener("click", () => this.hide())
        );

        if (this.form) {
          this.form.addEventListener("submit", this.onSubmit.bind(this));
        }
        if (this.emailInput) {
          this.emailInput.addEventListener("input", () => this.clearStatus());
        }

        this.onKeyDown = (event) => {
          if (event.key === "Escape" && this.hasAttribute("open")) this.hide();
        };

        // Delegated so triggers re-rendered by the Section Rendering API
        // (variant changes re-fetch the section) still open this drawer.
        this.onDelegatedClick = (event) => {
          const btn = event.target.closest(".mxl-bis-open-btn");
          if (btn) this.show(btn);
        };
      }

      connectedCallback() {
        if (this.moved) return;

        // Single-instance guard: if a live drawer already exists (this one
        // was re-injected by a section re-render), discard the newcomer.
        const existing = document.body.querySelector("mxl-bis-drawer[data-mxl-bis-primary]");
        if (existing && existing !== this) {
          this.remove();
          return;
        }

        this.moved = true;
        this.setAttribute("data-mxl-bis-primary", "");
        document.body.appendChild(this);

        document.addEventListener("click", this.onDelegatedClick);
        document.addEventListener("keydown", this.onKeyDown);

        // PUB_SUB_EVENTS is a top-level const — test the bare global, never
        // window.PUB_SUB_EVENTS (const declarations don't attach to window).
        if (
          typeof subscribe === "function" &&
          typeof PUB_SUB_EVENTS !== "undefined" &&
          PUB_SUB_EVENTS.variantChange
        ) {
          this.variantChangeUnsubscriber = subscribe(
            PUB_SUB_EVENTS.variantChange,
            (event) => {
              const variant = event && event.data && event.data.variant;
              if (variant) this.updateVariant(variant);
            }
          );
        }
      }

      disconnectedCallback() {
        if (!this.moved) return;
        document.removeEventListener("keydown", this.onKeyDown);
        document.removeEventListener("click", this.onDelegatedClick);
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
      }

      /* ---- variant state: one authoritative resolution path ---- */

      readFormVariantId() {
        const sectionId = this.dataset.sectionId;
        const selectors = sectionId
          ? [`#product-form-${sectionId} input[name="id"]`,
             `#sticky-bar-product-form-${sectionId} input[name="id"]`]
          : ['form[data-type="add-to-cart-form"] input[name="id"]'];
        for (const sel of selectors) {
          const input = document.querySelector(sel);
          if (input && input.value) return String(input.value);
        }
        return "";
      }

      getVariantData() {
        if (this._variantData) return this._variantData;
        const el = document.querySelector("[data-all-variants-no-high]");
        if (!el) return null;
        try {
          this._variantData = JSON.parse(el.textContent);
        } catch (e) {
          this._variantData = null;
        }
        return this._variantData;
      }

      /* Returns {id, variant, eligible}. eligible === true ONLY when the
         variant is resolved and confirmed NOT purchasable (available:false
         covers sold-out deny-policy variants and excludes continue-selling
         variants, which Shopify reports as available). When no variants JSON
         exists (single-variant products render no picker), fall back to the
         main submit button's sold-out state. */
      resolveCurrentVariant() {
        const id = this.readFormVariantId() || this.dataset.variantId || "";
        if (!id) return { id: "", variant: null, eligible: false };

        const data = this.getVariantData();
        if (data) {
          const variant = data.find((v) => String(v.id) === id) || null;
          return { id, variant, eligible: !!variant && variant.available === false };
        }

        const sectionId = this.dataset.sectionId;
        const submit = document.querySelector(
          sectionId
            ? `#product-form-${sectionId} .product-form__submit`
            : '.product-form__submit[name="add"]'
        );
        const soldOut =
          !!submit &&
          (submit.dataset.status === "sold-out" ||
            (submit.hasAttribute("disabled") && !submit.dataset.status));
        return { id, variant: null, eligible: soldOut };
      }

      syncFromResolved(resolved) {
        if (resolved.id) this.dataset.variantId = resolved.id;
        if (this.optionsEl && resolved.variant && resolved.variant.options) {
          this.optionsEl.textContent = resolved.variant.options.join(" / ");
        }
      }

      updateVariant(variant) {
        this.dataset.variantId = String(variant.id);
        if (this.optionsEl) {
          const text =
            variant.options && variant.options.length
              ? variant.options.join(" / ")
              : "";
          this.optionsEl.textContent = text;
        }
        this.clearStatus();
        this.setLoading(false);
      }

      /* ---- drawer UI ---- */

      show(trigger) {
        // Card mode: a collection-card Notify Me trigger carries the exact
        // product/variant on data attributes (set server-side from Liquid
        // availability, updated by the card dropdown handler). PDP triggers
        // have none of these and use the original form-resolution path.
        const cardData =
          trigger && trigger.dataset && trigger.dataset.mxlBisVariantId
            ? trigger.dataset
            : null;
        const titleEl = this.querySelector(
          ".avaliability-notification-product-title"
        );
        if (cardData) {
          this.cardMode = true;
          this.cardEligible = cardData.mxlBisAvailable === "false";
          this.dataset.variantId = cardData.mxlBisVariantId;
          if (titleEl) {
            if (this._pdpTitle === undefined) this._pdpTitle = titleEl.textContent;
            titleEl.textContent = cardData.mxlBisProductTitle || "";
          }
          if (this.optionsEl) {
            this.optionsEl.textContent = cardData.mxlBisVariantTitle || "";
          }
        } else {
          this.cardMode = false;
          if (titleEl && this._pdpTitle !== undefined) {
            titleEl.textContent = this._pdpTitle;
          }
          // Refresh variant context at open time; never trust page-load state.
          const resolved = this.resolveCurrentVariant();
          this.syncFromResolved(resolved);
        }
        this.clearStatus();
        this.setLoading(false);

        this.focusOrigin = trigger || document.activeElement;
        this.setAttribute("open", "");
        this.classList.add("modal--open");
        document.body.classList.add("overflow-hidden");

        const focusTarget = () => {
          if (typeof trapFocus === "function") {
            trapFocus(this, this.dialogEl);
          } else if (this.emailInput) {
            this.emailInput.focus();
          }
        };
        this.addEventListener("transitionend", focusTarget, { once: true });
        setTimeout(focusTarget, 250);
      }

      hide() {
        this.removeAttribute("open");
        this.classList.remove("modal--open");
        document.body.classList.remove("overflow-hidden");
        if (typeof removeTrapFocus === "function") {
          removeTrapFocus(this.focusOrigin);
        } else if (this.focusOrigin && this.focusOrigin.focus) {
          this.focusOrigin.focus();
        }
      }

      clearStatus() {
        if (!this.statusEl) return;
        this.statusEl.textContent = "";
        this.statusEl.removeAttribute("data-state");
        if (this.submitBtn) this.submitBtn.removeAttribute("disabled");
      }

      setStatus(state, message) {
        if (!this.statusEl) return;
        this.statusEl.dataset.state = state;
        this.statusEl.textContent = message;
      }

      setLoading(isLoading) {
        if (!this.submitBtn) return;
        const spinner = this.submitBtn.querySelector(
          ".loading-overlay__spinner"
        );
        if (isLoading) {
          this.submitBtn.setAttribute("disabled", "");
          this.submitBtn.setAttribute("aria-disabled", "true");
          if (spinner) spinner.classList.remove("hidden");
        } else {
          this.submitBtn.removeAttribute("aria-disabled");
          if (spinner) spinner.classList.add("hidden");
        }
      }

      seenKey(variantId, email) {
        return "mxl-bis:" + variantId + ":" + email.toLowerCase();
      }

      /* ---- submission ---- */

      async onSubmit(event) {
        event.preventDefault();

        const email = (this.emailInput && this.emailInput.value.trim()) || "";
        if (!email || !this.emailInput.checkValidity()) {
          this.emailInput.reportValidity();
          return;
        }

        // Re-resolve the variant IMMEDIATELY before submitting. The resolved
        // value — never a cached or first-available fallback — is what ships.
        // In card mode the variant was pinned by the card trigger at open time
        // and eligibility came from server-rendered Liquid availability.
        const resolved = this.cardMode
          ? {
              id: String(this.dataset.variantId || ""),
              variant: null,
              eligible: !!this.cardEligible,
            }
          : this.resolveCurrentVariant();
        if (!this.cardMode) this.syncFromResolved(resolved);

        const companyId = this.dataset.companyId;
        if (!resolved.id || !companyId) {
          this.setStatus("error", this.dataset.msgError);
          return;
        }
        if (!resolved.eligible) {
          // Selected variant is purchasable (or unresolvable) — refuse to
          // submit a Back in Stock request for it.
          this.setStatus("error", this.dataset.msgNotEligible || this.dataset.msgError);
          return;
        }
        const variantId = resolved.id;

        this.clearStatus();
        this.setLoading(true);
        this.setStatus("loading", this.dataset.msgLoading);

        let alreadySeen = false;
        try {
          alreadySeen = !!window.localStorage.getItem(
            this.seenKey(variantId, email)
          );
        } catch (e) {
          /* storage unavailable — treat as new request */
        }

        try {
          const response = await fetch(
            "https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=" +
              encodeURIComponent(companyId),
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                revision: "2025-04-15",
              },
              body: JSON.stringify({
                data: {
                  type: "back-in-stock-subscription",
                  attributes: {
                    channels: ["EMAIL"],
                    profile: {
                      data: {
                        type: "profile",
                        attributes: { email: email },
                      },
                    },
                  },
                  relationships: {
                    variant: {
                      data: {
                        type: "catalog-variant",
                        id: "$shopify:::$default:::" + variantId,
                      },
                    },
                  },
                },
              }),
            }
          );

          if (!response.ok) {
            this.setLoading(false);
            this.setStatus("error", this.dataset.msgError);
            return;
          }

          try {
            window.localStorage.setItem(this.seenKey(variantId, email), "1");
          } catch (e) {
            /* non-fatal */
          }

          // Marketing consent: separate, explicit, checkbox-gated. Never
          // inferred from the Back in Stock request itself.
          if (this.consentInput && this.consentInput.checked) {
            this.submitMarketingConsent(email, companyId);
          }

          this.setLoading(false);
          this.setStatus(
            alreadySeen ? "duplicate" : "success",
            alreadySeen ? this.dataset.msgDuplicate : this.dataset.msgSuccess
          );
          if (this.submitBtn) this.submitBtn.setAttribute("disabled", "");
        } catch (error) {
          this.setLoading(false);
          this.setStatus("error", this.dataset.msgError);
        }
      }

      submitMarketingConsent(email, companyId) {
        const listId = this.dataset.marketingListId;
        if (!listId) return;

        const payload = {
          data: {
            type: "subscription",
            attributes: {
              custom_source:
                "Back in Stock drawer - marketing opt-in checkbox (checked)",
              profile: {
                data: {
                  type: "profile",
                  attributes: {
                    email: email,
                    subscriptions: {
                      email: { marketing: { consent: "SUBSCRIBED" } },
                    },
                  },
                },
              },
            },
            relationships: {
              list: { data: { type: "list", id: listId } },
            },
          },
        };

        fetch(
          "https://a.klaviyo.com/client/subscriptions/?company_id=" +
            encodeURIComponent(companyId),
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              revision: "2025-04-15",
            },
            body: JSON.stringify(payload),
          }
        ).catch(() => {
          /* consent call failure must not break the BIS success state;
             the subscription can be retried on a future submission */
        });
      }
    }
  );
}
